/**
 * Player controller (P0-T09): movement input (WASD/arrows + Shift run on desktop,
 * joystick + RUN on touch) drives the physics worker; the local capsule mesh is
 * driven by worker state. `move` messages go out at 20 Hz (MOVE_RATE_HZ) and a
 * server `correct` snaps us back (N-03).
 */
import { INTERPOLATION_DELAY_MS, MOVE_RATE_HZ, type ClockSync } from '@kota-nova/shared';
import type { KNGlobal } from '../debug/kn';
import type { PlayerPhysState, PhysOut } from '../workers/physics-protocol';

export interface PlayerHandle {
  /** capsule root mesh (visual stand-in for the avatar) */
  root: unknown;
  setInput(fwd: number, turn: number, strafeX: number, strafeZ: number, run: boolean): void;
  update(dtMs: number, nowMs: number): void;
  readonly state: PlayerPhysState;
  sendCorrect(c: { x: number; y: number; z: number; seq: number }): void;
  teleport(x: number, y: number, z: number): void;
  /** Streamed per-cell static colliders (buildings, fountains). */
  sendColliders(
    op: 'add' | 'remove',
    cell: string,
    boxes: Array<[number, number, number, number, number, number]>,
  ): void;
  dispose(): void;
}

interface BabylonRefs {
  babylon: typeof import('babylonjs');
  scene: import('babylonjs').Scene;
}

export function createPlayer(
  refs: BabylonRefs,
  opts: {
    send: (type: string, payload: unknown) => void;
    getClock: () => ClockSync | null;
    kn?: KNGlobal;
  },
): PlayerHandle {
  const { babylon, scene } = refs;
  const root = babylon.MeshBuilder.CreateCapsule('player', { radius: 0.34, height: 1.75 }, scene);
  const mat = new babylon.StandardMaterial('playerMat', scene);
  mat.diffuseColor = new babylon.Color3(0.84, 0.71, 0.43);
  mat.emissiveColor = new babylon.Color3(0.08, 0.07, 0.04);
  root.material = mat;

  const worker = new Worker(new URL('../workers/physics.worker.ts', import.meta.url), { type: 'module' });

  let state: PlayerPhysState = { x: 0, y: 0, z: 0, grounded: true, yaw: null, anim: 0, speed: 0 };
  let seq = 0;
  let lastSend = 0;

  worker.onmessage = (ev: MessageEvent<PhysOut>) => {
    const msg = ev.data;
    if (msg.t === 'state') state = msg.state;
  };
  worker.postMessage({ t: 'init', tier: 'mid', obstacles: TEST_OBSTACLES });

  function sendMove(): void {
    seq += 1;
    opts.send('move', {
      x: state.x,
      y: state.y,
      z: state.z,
      yaw: state.yaw ?? 0,
      anim: state.anim,
      speed: state.speed,
      seq,
    });
  }

  return {
    root,
    setInput(fwd: number, turn: number, strafeX: number, strafeZ: number, run: boolean): void {
      worker.postMessage({ t: 'setInput', fwd, turn, strafeX, strafeZ, run });
    },
    update(dtMs: number, nowMs: number): void {
      worker.postMessage({ t: 'step', dtMs });
      root.position.set(state.x, state.y + 0.875, state.z);
      if (state.yaw !== null) root.rotation.y = state.yaw;
      if (nowMs - lastSend >= 1000 / MOVE_RATE_HZ) {
        lastSend = nowMs;
        sendMove();
      }
    },
    get state() {
      return state;
    },
    sendCorrect(c) {
      // server says our last accepted position differs: snap the capsule back and
      // zero the input for one frame feel (interpolation delay covers the rest)
      worker.postMessage({ t: 'teleport', x: c.x, y: c.y, z: c.z });
      state = { ...state, x: c.x, y: c.y, z: c.z };
      seq = Math.max(seq, c.seq);
    },
    teleport(x, y, z) {
      worker.postMessage({ t: 'teleport', x, y, z });
    },
    sendColliders(op, cell, boxes) {
      worker.postMessage({ t: 'statics', op, cell, boxes });
    },
    dispose() {
      worker.terminate();
      root.dispose();
      mat.dispose();
    },
  };
}

/** P0 test course: a 0.30 m step (climbable) and a 0.50 m step (blocked). */
export const TEST_OBSTACLES: Array<[number, number, number, number, number, number]> = [
  [4, 0.15, 0, 2, 0.15, 2], // 0.30 m step
  [10, 0.25, 0, 2, 0.25, 2], // 0.50 m step
];

/** Remote avatars: buffer of server patches interpolated 100 ms in the past. */
export class RemoteAvatarPool {
  private readonly babylon: typeof import('babylonjs');
  private readonly scene: import('babylonjs').Scene;
  private readonly meshes = new Map<string, import('babylonjs').Mesh>();
  private readonly buffers = new Map<
    string,
    Array<{ t: number; x: number; y: number; z: number; yaw: number; anim: number }>
  >();
  private readonly delayMs = INTERPOLATION_DELAY_MS;

  constructor(babylon: typeof import('babylonjs'), scene: import('babylonjs').Scene) {
    this.babylon = babylon;
    this.scene = scene;
  }

  /** Feed one player entity update from the Colyseus state. */
  push(
    id: string,
    x: number,
    y: number,
    z: number,
    yawRad: number,
    anim: number,
    nowMs: number,
    colorHex: string,
  ): void {
    const buf = this.buffers.get(id) ?? [];
    buf.push({ t: nowMs, x, y, z, yaw: yawRad, anim });
    if (buf.length > 40) buf.shift();
    this.buffers.set(id, buf);

    let mesh = this.meshes.get(id);
    if (!mesh) {
      mesh = this.babylon.MeshBuilder.CreateCapsule(`p-${id}`, { radius: 0.34, height: 1.75 }, this.scene);
      const m = new this.babylon.StandardMaterial(`pm-${id}`, this.scene);
      const c = this.babylon.Color3.FromHexString(colorHex || '#d6b56d');
      m.diffuseColor = c;
      m.emissiveColor = new this.babylon.Color3(c.r * 0.1, c.g * 0.1, c.b * 0.1);
      mesh.material = m;
      this.meshes.set(id, mesh);
    }
  }

  /** Interpolate all remotes at (now - 100 ms). */
  update(nowMs: number): void {
    const renderT = nowMs - this.delayMs;
    for (const [id, mesh] of this.meshes) {
      const buf = this.buffers.get(id);
      if (!buf || buf.length === 0) continue;
      // find the two samples around renderT
      let a = buf[0]!;
      let b = buf[buf.length - 1]!;
      for (let i = 0; i < buf.length - 1; i++) {
        if (buf[i]!.t <= renderT && buf[i + 1]!.t >= renderT) {
          a = buf[i]!;
          b = buf[i + 1]!;
          break;
        }
      }
      const span = Math.max(1, b.t - a.t);
      const f = Math.max(0, Math.min(1, (renderT - a.t) / span));
      mesh.position.set(a.x + (b.x - a.x) * f, a.y + (b.y - a.y) * f + 0.875, a.z + (b.z - a.z) * f);
      mesh.rotation.y = a.yaw + (b.yaw - a.yaw) * f;
    }
  }

  /** P1-T10: current interpolated positions for blob shadows (Low tier). */
  forEachPosition(cb: (id: string, x: number, z: number) => void): void {
    for (const [id, mesh] of this.meshes) cb(id, mesh.position.x, mesh.position.z);
  }

  /** P1-T10: remote capsule meshes (shadow casters on mid/high tiers). */
  casterMeshes(): import('babylonjs').Mesh[] {
    return [...this.meshes.values()];
  }

  remove(id: string): void {
    this.meshes.get(id)?.dispose();
    this.meshes.delete(id);
    this.buffers.delete(id);
  }

  dispose(): void {
    for (const m of this.meshes.values()) m.dispose();
    this.meshes.clear();
    this.buffers.clear();
  }
}
